/**
 * ChartUtilitiesHelper
 * 
 * Static utility methods for chart data processing and formatting.
 * 
 * Pure helper methods that don't modify component state.
 * Main chart orchestration methods remain in component.
 */

import { CrossTabOutputItem, checkboxSelectionWithParent, LineChart, StackChart, GroupBarChart } from 'src/app/common/models/crosstab.model';
import { Status } from 'src/app/common/models/enum/status.enum';

export class ChartUtilitiesHelper {
  
  /**
   * Get share number based on base type and weight selection
   */
  static getShareNumber(
    item: CrossTabOutputItem,
    isTotalBase: boolean,
    selectedWeight: string
  ): number | null {
    return isTotalBase
      ? (selectedWeight === 'Unweighted' ? item.totalUnweightedShare : item.totalWeightedShare)
      : (selectedWeight === 'Unweighted' ? item.answerUnweightedShare : item.answerWeightedShare);
  }

  /**
   * Calculate percentage value from share number
   */
  static calculatePercentageValue(
    shareNumber: number | null,
    item: CrossTabOutputItem
  ): number | string {
    if (shareNumber === null) return 'NA';
    return item.isDerived && item.CalculationType === 'mean'
      ? Number(shareNumber.toFixed(1))
      : Number((shareNumber * 100).toFixed(1));
  }

  /**
   * Get change vs previous year value
   */
  static getChangeVsPY(
    item: CrossTabOutputItem,
    isTotalBase: boolean
  ): number | string {
    const changevsPY = isTotalBase ? item.changeVsPY_Total : item.changeVsPY_Answer;
    if (changevsPY === null) return 'NA';
    return item.isDerived && item.CalculationType === 'mean'
      ? Number(changevsPY.toFixed(1))
      : Number((changevsPY * 100).toFixed(1));
  }

  /**
   * Get significance value as string
   */
  static getSignificanceString(
    item: CrossTabOutputItem,
    isTotalBase: boolean
  ): string {
    const sig = isTotalBase ? item.Significance_Total : item.Significance_Answer;
    return sig === null ? '0' : sig.toString();
  }

  /**
   * Get sample size value as string
   */
  static getSampleSizeString(
    item: CrossTabOutputItem,
    isTotalBase: boolean
  ): string {
    const base = isTotalBase ? item.totalUnweightedBase : item.answerUnweightedBase;
    return base === null ? '0' : base.toString();
  }

  /**
   * Get distinct formatted child values from data object
   */
  static getDistinctChildValuesFormatted(
    dataObject: checkboxSelectionWithParent[]
  ): string[] {
    return Array.from(
      new Set(dataObject.flatMap(parent => parent.Data.map(selection => selection.Child)))
    ).map(child => child.replaceAll('|', ' : '));
  }

  /**
   * Filter data by calculation type
   */
  static filterDataByCalculationType(
    data: checkboxSelectionWithParent[],
    type: 'multipunch' | 'respgrp' | 'mean'
  ): checkboxSelectionWithParent[] {
    return data.filter(parent => {
      return parent.Data.some(child => {
        switch (type) {
          case 'multipunch': return child.isMultiPunch === Status.MultiPunchDisableChart;
          case 'respgrp': return child.CalculationType === 'RespGrp' && child.isDerviedVariable === true;
          case 'mean': return child.CalculationType.toLowerCase() === 'mean' && child.isDerviedVariable === true;
        }
      });
    });
  }

  /**
   * Initialize chart data and properties arrays
   */
  static initializeChartData(
    chart: LineChart | StackChart | GroupBarChart
  ): void {
    chart.Data = [];
    chart.Properties = [];
  }

  /**
   * Build common chart properties object
   */
  static buildCommonChartProperties(
    distinctColChildValues: string[],
    numberType: string,
    chartStyle: string = '',
    DefaultSampleSize: string = 'None',
    CrossTabOutPutData: any[] = []
  ): any {
    return {
      plotBands: false,
      lowSample: false,
      showNegative: false,
      allowOverlap: chartStyle === 'area' ? false : true,
      chartStyle: chartStyle,
      showLegend: true,
      gridlines: false,
      shadow: false,
      stacking: 0,
      darkTheme: false,
      legendPosition: 'bottom',
      axisLabelOrientation: chartStyle === 'area' ? 'center' : 'horizontal',
      chartTitlePosition: 'center',
      showDataLabel: true,
      datalabelStyle: DefaultSampleSize === 'None' ? (chartStyle === 'area' ? '' : 'datalabel') : (chartStyle === 'area' ? 'sigchan' : 'change'),
      axisImage: false,
      dataLabelPosition: 'center',
      dataLabelColor: 'black',
      marker: 'disable',
      showRemaining: false,
      gradientBarColor: false,
      showPattern: false,
      dataLabelPositionPPT: chartStyle === 'area' ? 'center' : (chartStyle === '' ? 'Center' : 'Inside Base'),
      datalabelFormat: numberType === CrossTabOutPutData?.[0]?.rowChild?.toLowerCase().includes('mean') ? (chartStyle === 'area' ? 'number' : '') : 'percentage',
      datalabelOrientation: 0,
      lineWidth: chartStyle === 'area' ? 2 : 0,
      gridLineDashStyle: chartStyle === 'area' ? 'Solid' : '',
      markerLineWidth: chartStyle === 'area' ? 1 : 0,
      markerStyle: 'circle',
      title: chartStyle === 'area' ? 'LineChart' : '',
      toolTipStyle: 'default',
      ChartCat: distinctColChildValues
    };
  }

  /**
   * Sort chart data by series name order
   */
  static sortChartDataBySeriesName(
    chartData: any[],
    distinctRowChildValues: string[]
  ): void {
    chartData.sort((a, b) => {
      const indexA = distinctRowChildValues.findIndex(item => item === a['Series Name']);
      const indexB = distinctRowChildValues.findIndex(item => item === b['Series Name']);
      return indexA - indexB;
    });
  }

  /**
   * Get distinct values by pipe count
   */
  static getDistinctValuesByPipeCount(
    data: string[],
    pipeCount: number
  ): string[] {
    const distinctValues = new Set<string>();
    data.forEach((item) => {
      const total = (item.match(/\|/g) || []).length;
      if (pipeCount <= total) {
        const parts = item.split('|')[pipeCount];
        distinctValues.add(parts.trim());
      }
    });
    return Array.from(distinctValues);
  }

  /**
   * Get distinct pipe parts from row values
   */
  static getDistinctPipeParts(
    rowValues: string[]
  ): { first: string[], second: string[], third: string[], fourth: string[], fifth: string[] } {
    return {
      first: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 0),
      second: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 1),
      third: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 2),
      fourth: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 3),
      fifth: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 4)
    };
  }

  /**
   * Calculate if stack chart should be enabled
   */
  static calculateStackFlag(
    parts: { first: string[], second: string[], third: string[], fourth: string[], fifth: string[] }
  ): boolean {
    return (parts.fifth.length > 0 && parts.fourth.length == 1 && parts.third.length == 1 && parts.second.length == 1 && parts.first.length == 1) ||
      (parts.fourth.length > 0 && parts.third.length == 1 && parts.second.length == 1 && parts.first.length == 1) ||
      (parts.third.length > 0 && parts.second.length == 1 && parts.first.length == 1) ||
      (parts.second.length > 0 && parts.first.length == 1);
  }

  /**
   * Get distinct column child values
   */
  static getDistinctColumnChildValues(
    dataObject: { Column: checkboxSelectionWithParent[] }
  ): string[] {
    const distinctColumnChildren = Array.from(new Set(dataObject.Column.flatMap((parent) => parent.Data.map((selection) => selection.Child))));
    return distinctColumnChildren.map((child) => child.replaceAll('|', ' : '));
  }

  /**
   * Filter variable data by pipe levels
   */
  static filterByPipeLevels(
    child: string,
    variableTabData: any[]
  ): any[] {
    const levels = child.split('|');
    const pipeCount = levels.length - 1;

    return variableTabData.filter((x: any) => {
      if (x.Header !== 'VariableList') return false;

      const levelChecks = [
        () => x.Level1 === levels[0],
        () => x.Level2 === levels[1],
        () => x.level3 === levels[2],
        () => x.level4 === levels[3],
        () => x.level5 === levels[4]
      ];

      return levelChecks.slice(0, pipeCount + 1).every(check => check());
    });
  }

  /**
   * Get distinct child values from data array
   */
  static getDistinctChildValues(
    data: checkboxSelectionWithParent[]
  ): string[] {
    const distinctChildValues: string[] = [];
    data.forEach((item) => {
      item.Data.forEach((checkbox) => {
        if (!distinctChildValues.includes(checkbox.Child)) {
          distinctChildValues.push(checkbox.Child);
        }
      });
    });

    return distinctChildValues;
  }
}
